import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ConditionalLogicEntity } from '@elfsight/widget-form-builder/widget';
import { useConditionalLogic } from '@elfsight/widget-form-builder/widget';
import { useSettings } from '@wu/widget-core';
import type { CalculationType, CalculatorField } from '../types';
import { useFieldsScope } from './use-fields-scope';

type DisplayedFieldsType = CalculatorField | CalculationType;

type ConditionalLogicContextType = {
  displayedFields: CalculatorField[];
  displayedCalculations: CalculationType[];
};

type CalculatorConditionalFieldsType =
  ConditionalLogicEntity<CalculationType>[];

const ConditionalLogicContext = createContext<ConditionalLogicContextType>({
  displayedFields: [],
  displayedCalculations: []
});

export function ConditionalLogicProvider({ children }: PropsWithChildren) {
  const { fields, calculations } = useSettings();
  const scope = useFieldsScope();

  const currentCalculations = useMemo(
    () =>
      calculations.map((calculation: CalculationType) => ({
        ...calculation,
        type: calculation.type || 'calculation'
      })),
    [calculations]
  );

  const conditionalFields = useConditionalLogic<CalculationType>(
    [...fields, ...currentCalculations] as CalculatorConditionalFieldsType,
    scope
  );

  const value = useMemo(() => {
    const initialResult = { displayedFields: [], displayedCalculations: [] };

    return conditionalFields.reduce(
      (result: ConditionalLogicContextType, field: DisplayedFieldsType) => {
        if (field.type === 'calculation') {
          result.displayedCalculations.push(field);
        } else {
          result.displayedFields.push(field);
        }

        return result;
      },
      initialResult
    );
  }, [conditionalFields]);

  return (
    <ConditionalLogicContext.Provider value={value}>
      {children}
    </ConditionalLogicContext.Provider>
  );
}

export const useCalculatorConditionalLogic = () => {
  return useContext(ConditionalLogicContext);
};
